/**
 * WorkflowStatusSidebar
 *
 * Simplified right sidebar for Setup and ExistingDebt workflow pages.
 * Supports three display modes:
 * 1. Small (icons only, ~56px)
 * 2. Normal (icons + labels with status colors, ~200px)
 * 3. Expanded (50% viewport width with tabbed data tables - max 10 rows each, scrollable)
 *
 * Status indicators are tri-state:
 * - Empty (gray): No data entered
 * - Partial (yellow): Some data entered, not calculated
 * - Complete (green): Data entered and calculated
 *
 * Usage:
 * ```tsx
 * <SidebarProvider>
 *   <PageLayoutGrid rightSidebar={<WorkflowStatusSidebar projectId={projectId} />}>
 *     <YourPageContent />
 *   </PageLayoutGrid>
 * </SidebarProvider>
 * ```
 */

import { Box, Button, HStack, IconButton, Tabs, Text, VStack } from '@chakra-ui/react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileText,
  Landmark,
  TrendingUp,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { ExistingDebtCumulativeTable } from '~/builders/existing-debt/ExistingDebtCumulativeTable';
import { DateTable } from '~/builders/project-dates/DateTable';
import { TaxableValueTable } from '~/builders/taxable-value/TaxableValueTable';
import { SimpleValueDisplay } from '~/components/display/SimpleValueDisplay';
import { useSidebar } from '~/components/layout/shared/SidebarContext';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { useDateMaps } from '~/hooks/useDateMaps';
import { isBondSeriesComplete } from '~/hooks/useExistingDebtHandlers';
import { useProjectStore } from '~/hooks/useProjectStore';
import { type SectionStatus, useWorkflowStatus } from '~/hooks/useWorkflowStatus';
import { BuildService } from '~/services/build-service';
import type { DateInputsForm } from '~/types/form-types';
import type {
  DateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
} from '~/types/project';
import { formatCurrency } from '~/utils/formatting-utils';
import { parseFormattedNumber } from '~/utils/numberParsing';

// Extended DateRecord with index signature for table compatibility
type DateRecordWithIndex = DateRecord & Record<string, unknown>;

type TabSection = 'dates' | 'tax' | 'debt' | 'millage' | 'drf';

// Status color mappings - extracted to constants to prevent recalculation on every render
const STATUS_BG_COLORS: Record<SectionStatus, string> = {
  empty: 'gray.200',
  partial: 'yellow.400',
  complete: 'green.500',
};

const STATUS_HOVER_BG_COLORS: Record<SectionStatus, string> = {
  empty: 'gray.300',
  partial: 'yellow.500',
  complete: 'green.600',
};

const STATUS_TEXT_COLORS: Record<SectionStatus, string> = {
  empty: 'gray.500',
  partial: 'black',
  complete: 'white',
};

export interface WorkflowStatusSidebarProps {
  /** Project ID to load data for */
  projectId: string;
}

/**
 * WorkflowStatusSidebar Component
 *
 * Simplified version:
 * - Three display states: small (icons), normal (icons+labels), expanded (50% width with tabbed data tables)
 * - All tables show max 10 rows and are scrollable
 * - Tri-state status indicators (empty, partial, complete)
 * - Shows debt icon/tab when on existing debt page or if project has existing debt
 */
export const WorkflowStatusSidebar: React.FC<WorkflowStatusSidebarProps> = ({ projectId }) => {
  const { sidebarMode, setSidebarMode } = useSidebar();
  const location = useLocation();

  // Auto-select debt tab if on existing debt page, otherwise default to dates
  const initialTab: TabSection = location.pathname.includes('/existing-debt') ? 'debt' : 'dates';
  const [selectedTab, setSelectedTab] = useState<TabSection>(initialTab);

  // Track previous pathname to detect navigation changes
  const prevPathnameRef = useRef(location.pathname);

  // Sync selected tab when location changes (only depend on pathname, not selectedTab)
  useEffect(() => {
    const isOnDebtPage = location.pathname.includes('/existing-debt');
    const wasOnDebtPage = prevPathnameRef.current.includes('/existing-debt');

    // Only update if pathname actually changed
    if (location.pathname !== prevPathnameRef.current) {
      if (isOnDebtPage && !wasOnDebtPage) {
        // Navigated to debt page - switch to debt tab
        setSelectedTab('debt');
      } else if (!isOnDebtPage && wasOnDebtPage) {
        // Navigated away from debt page - switch to dates tab
        setSelectedTab('dates');
      }
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Get data from store (only what's needed for rendering, status calculation is in hook)
  // Zustand v5: useShallow wraps an INLINE selector (not a memoized callback)
  const {
    dateRecords,
    dateInputs,
    taxValueMap,
    existingDebtProjects,
    millageInputs,
    drfInputs,
    bondInterestRateSettings,
    hasExistingDebt,
  } = useProjectStore(
    useShallow((s) => ({
      dateRecords: s.getDateRecords(projectId),
      dateInputs: s.getDateInputsForm(projectId),
      taxValueMap: s.getTaxableValueMap(projectId),
      existingDebtProjects: s.getExistingDebtProjects(projectId),
      millageInputs: s.getMillageInputsForm(projectId),
      drfInputs: s.getDRFInputsForm(projectId),
      bondInterestRateSettings: s.projects[projectId]?.bondInterestRateSettings,
      hasExistingDebt: s.projects[projectId]?.metadata?.hasExistingDebt ?? false,
    }))
  );

  // Check if we're on the existing debt page
  const isOnExistingDebtPage = location.pathname.includes('/existing-debt');

  // Get workflow statuses from hook
  const sectionStatuses = useWorkflowStatus(projectId);

  // Extract primitive status values to prevent infinite loop
  // CRITICAL: Using sectionStatuses object directly in useMemo dependencies causes infinite loops
  const datesStatus = sectionStatuses.dates;
  const taxStatus = sectionStatuses.tax;
  const millageStatus = sectionStatuses.millage;
  const drfStatus = sectionStatuses.drf;
  const debtStatus = sectionStatuses.debt;

  // Generate date map formats for DateTable using extracted hook (only when expanded and dates tab is selected)
  const dateMapsResult = useDateMaps(
    dateRecords,
    dateInputs,
    sidebarMode === 'expanded' && selectedTab === 'dates'
  );
  const dateRecordsMaps = {
    byTaxYear: dateMapsResult.byTaxYear,
    byPaymentDate: dateMapsResult.byPaymentDate,
  };
  const dateMapError = dateMapsResult.error;

  // Icon button configurations
  // CRITICAL: Depend on primitive status values, NOT the sectionStatuses object
  const iconButtons = useMemo(
    () => [
      { id: 'dates' as TabSection, icon: Calendar, label: 'Dates', status: datesStatus },
      { id: 'tax' as TabSection, icon: TrendingUp, label: 'Tax Values', status: taxStatus },
      { id: 'millage' as TabSection, icon: DollarSign, label: 'Millage', status: millageStatus },
      { id: 'drf' as TabSection, icon: Landmark, label: 'DRF', status: drfStatus },
      { id: 'debt' as TabSection, icon: FileText, label: 'Existing Debt', status: debtStatus },
    ],
    [datesStatus, taxStatus, millageStatus, drfStatus, debtStatus]
  );

  // Use constant mappings instead of functions to prevent recalculation
  const getBgForStatus = (status: SectionStatus) => STATUS_BG_COLORS[status];
  const getHoverBgForStatus = (status: SectionStatus) => STATUS_HOVER_BG_COLORS[status];
  const getTextColorForStatus = (status: SectionStatus) => STATUS_TEXT_COLORS[status];

  const handleIconClick = (section: TabSection) => {
    if (sidebarMode === 'expanded') {
      // In expanded mode, clicking an icon switches tabs
      setSelectedTab(section);
    } else {
      // In small/normal mode, clicking expands to that tab
      setSelectedTab(section);
      setSidebarMode('expanded');
    }
  };

  const toggleSidebarMode = () => {
    // Toggle only between small and normal (not expanded)
    if (sidebarMode === 'small') {
      setSidebarMode('normal');
    } else {
      setSidebarMode('small');
    }
  };

  const closeExpanded = () => {
    setSidebarMode('normal');
  };

  // Calculate sidebar width: expanded is 50% of viewport, otherwise use fixed widths
  const sidebarWidth =
    sidebarMode === 'small'
      ? LAYOUT_SPACING.SIDEBAR_COLLAPSED
      : sidebarMode === 'expanded'
        ? '50vw'
        : LAYOUT_SPACING.SIDEBAR_EXPANDED_SM;

  return (
    <Box
      width={typeof sidebarWidth === 'string' ? sidebarWidth : `${sidebarWidth}px`}
      minWidth={typeof sidebarWidth === 'string' ? sidebarWidth : `${sidebarWidth}px`}
      height={sidebarMode === 'expanded' ? `100vh` : '100%'}
      maxHeight={sidebarMode === 'expanded' ? `100vh` : undefined}
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      position={sidebarMode === 'expanded' ? 'sticky' : 'relative'}
      top={sidebarMode === 'expanded' ? 0 : undefined}
      transition={`width ${LAYOUT_SPACING.TRANSITION_NORMAL}, min-width ${LAYOUT_SPACING.TRANSITION_NORMAL}`}
      overflow="hidden"
      boxShadow="lg"
    >
      {/* Content */}
      {sidebarMode === 'expanded' ? (
        // Expanded mode: Tabs with data tables
        <Box height="100%" display="flex" flexDirection="column" overflow="hidden">
          {/* Header with close button */}
          <Box
            pt={LAYOUT_SPACING.CARD_PADDING}
            px={LAYOUT_SPACING.CARD_PADDING}
            pb={LAYOUT_SPACING.CARD_PADDING}
            borderBottom="1px solid"
            borderColor="gray.200"
            flexShrink={0}
          >
            <HStack justify="space-between" align="center" position="relative">
              <IconButton
                aria-label="Close expanded sidebar"
                size="sm"
                variant="ghost"
                onClick={closeExpanded}
                position="absolute"
                left={0}
              >
                <ChevronRight size={16} />
              </IconButton>
              <Text
                fontSize="md"
                fontWeight="semibold"
                color="gray.800"
                flex={1}
                textAlign="center"
              >
                Data Validation
              </Text>
              <Box width="32px" /> {/* Spacer to balance close button */}
            </HStack>
          </Box>

          {/* Tabs with data tables */}
          <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minHeight={0}>
            <Tabs.Root
              value={selectedTab}
              onValueChange={(e) => setSelectedTab(e.value as TabSection)}
              height="100%"
              display="flex"
              flexDirection="column"
            >
              <Tabs.List
                px={LAYOUT_SPACING.CARD_PADDING_SM}
                pt={LAYOUT_SPACING.CARD_PADDING_SM}
                borderBottom="1px solid"
                borderColor="gray.200"
                flexShrink={0}
              >
                {iconButtons.map((button) => {
                  // Show debt tab if on existing debt page OR if project has existing debt
                  if (button.id === 'debt' && !hasExistingDebt && !isOnExistingDebtPage) {
                    return null;
                  }

                  return (
                    <Tabs.Trigger
                      key={button.id}
                      value={button.id}
                      disabled={button.status === 'empty'}
                      opacity={button.status === 'empty' ? 0.5 : 1}
                    >
                      <button.icon size={16} />
                      <Text fontSize="xs" ml={1}>
                        {button.label}
                      </Text>
                    </Tabs.Trigger>
                  );
                })}
              </Tabs.List>

              <Box
                flex={1}
                overflowY="auto"
                overflowX="hidden"
                p={LAYOUT_SPACING.CARD_PADDING}
                minHeight={0}
                bg="white"
              >
                <Tabs.Content value="dates">
                  {dateMapError ? (
                    <Box p={4}>
                      <Text color="red.500" fontSize="sm" fontWeight="medium">
                        Error loading date data: {dateMapError}
                      </Text>
                    </Box>
                  ) : (
                    dateRecords && (
                      <DateTable
                        data={dateRecords as DateRecordWithIndex[]}
                        dateRecordsMapByTaxYear={
                          dateRecordsMaps.byTaxYear as unknown as DateRecordsMapByTaxYear
                        }
                        dateRecordsMapByPaymentDate={
                          dateRecordsMaps.byPaymentDate as unknown as DateRecordsMapByPaymentDate
                        }
                        maxRows={LAYOUT_SPACING.TABLE_DEFAULT_ROWS}
                      />
                    )
                  )}
                </Tabs.Content>

                <Tabs.Content value="tax">
                  {taxValueMap && <TaxableValueTable taxableValueMap={taxValueMap} />}
                </Tabs.Content>

                <Tabs.Content value="millage">
                  {millageInputs && (
                    <SimpleValueDisplay
                      values={[
                        {
                          label: 'Peak Millage',
                          value: millageInputs.peakMillage || '',
                          formatter: (val) => (val ? `${val} mills` : '—'),
                        },
                        {
                          label: 'Peak Millage Duration',
                          value: millageInputs.peakMillageDuration || '',
                          formatter: (val) => (val ? `${val} year${val !== '1' ? 's' : ''}` : '—'),
                        },
                        {
                          label: 'Minimum Millage',
                          value: millageInputs.minimumMillage || '',
                          formatter: (val) => (val ? `${val} mills` : '—'),
                        },
                        {
                          label: 'Minimum Millage Duration',
                          value: millageInputs.minimumMillageDuration || '',
                          formatter: (val) => (val ? `${val} year${val !== '1' ? 's' : ''}` : '—'),
                        },
                      ]}
                    />
                  )}
                </Tabs.Content>

                <Tabs.Content value="drf">
                  <VStack align="stretch" gap={4}>
                    {drfInputs && (
                      <SimpleValueDisplay
                        values={[
                          {
                            label: 'Starting Balance',
                            value: drfInputs.startingBalance || '',
                            formatter: (val) => {
                              if (!val) return '—';
                              const parsed = parseFormattedNumber(String(val));
                              return Number.isFinite(parsed)
                                ? formatCurrency(parsed, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : '—';
                            },
                          },
                          {
                            label: 'Minimum Balance',
                            value: drfInputs.minimumBalance || '',
                            formatter: (val) => {
                              if (!val) return '—';
                              const parsed = parseFormattedNumber(String(val));
                              return Number.isFinite(parsed)
                                ? formatCurrency(parsed, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : '—';
                            },
                          },
                          {
                            label: 'Maximum Balance',
                            value: drfInputs.maximumBalance || '',
                            formatter: (val) => {
                              if (!val) return '—';
                              const parsed = parseFormattedNumber(String(val));
                              return Number.isFinite(parsed)
                                ? formatCurrency(parsed, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : '—';
                            },
                          },
                        ]}
                      />
                    )}

                    {/* Bond Interest Rates Section */}
                    {bondInterestRateSettings && (
                      <Box pt={4} borderTop="1px solid" borderColor="gray.200">
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>
                          Bond Interest Rates
                        </Text>
                        <SimpleValueDisplay
                          values={[
                            {
                              label: 'Start Rate',
                              value: bondInterestRateSettings.startRate.toString(),
                              formatter: (val) => (val ? `${val}%` : '—'),
                            },
                            {
                              label: 'Increment',
                              value: bondInterestRateSettings.incrementRate.toString(),
                              formatter: (val) => (val ? `${val}%` : '—'),
                            },
                            {
                              label: 'Max Rate',
                              value: bondInterestRateSettings.maxRate.toString(),
                              formatter: (val) => (val ? `${val}%` : '—'),
                            },
                          ]}
                        />
                      </Box>
                    )}
                  </VStack>
                </Tabs.Content>

                <Tabs.Content value="debt">
                  {(() => {
                    // Get current state to check what to show
                    const currentProject = useProjectStore.getState().projects[projectId];
                    const debtInputs = currentProject?.existingDebtInputs;
                    const currentDateRecords = currentProject?.dateRecords;
                    const hasCalculatedData =
                      existingDebtProjects && existingDebtProjects.length > 0;

                    // Check if we have at least one COMPLETE debt project
                    // A project is complete if it has id, name, payments, and valid bond series completion
                    const completeProjects =
                      debtInputs?.filter((form) => {
                        if (
                          !form.id?.trim() ||
                          !form.name?.trim() ||
                          !form.payments ||
                          form.payments.length === 0
                        ) {
                          return false;
                        }
                        return isBondSeriesComplete(form);
                      }) || [];
                    const hasCompleteInputs = completeProjects.length > 0;

                    // Case 1: No complete debt inputs
                    if (!hasCompleteInputs) {
                      return (
                        <VStack align="stretch" gap={4}>
                          <Text fontSize="sm" color="gray.600">
                            No existing debt projects have been entered yet.
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Add at least one complete debt project (with ID, name, and payments) on
                            the Existing Debt page.
                          </Text>
                        </VStack>
                      );
                    }

                    // Case 2: Has calculated data - show table
                    if (hasCalculatedData) {
                      return (
                        <ExistingDebtCumulativeTable
                          existingDebtProjects={existingDebtProjects}
                          dateInputs={currentProject?.dateInputs as DateInputsForm}
                          dateRecords={currentDateRecords as DateRecord[]}
                        />
                      );
                    }

                    // Case 3: Has complete inputs but no calculations - show button
                    return (
                      <VStack align="stretch" gap={4}>
                        <Text fontSize="sm" color="gray.600">
                          Cumulative debt payment schedule has not been calculated yet.
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {completeProjects.length} debt project
                          {completeProjects.length !== 1 ? 's' : ''} ready to calculate.
                        </Text>
                        <Button
                          size="sm"
                          colorPalette="blue"
                          onClick={() => {
                            // Use BuildService for centralized building
                            const result = BuildService.buildExistingDebtProjects(projectId);

                            if (!result) {
                              console.error('[Sidebar] Failed to build cumulative debt');
                              // Could show error toast here
                            }
                          }}
                          disabled={!currentDateRecords}
                        >
                          Calculate Cumulative Debt Payments
                        </Button>
                        {!currentDateRecords && (
                          <Text fontSize="xs" color="orange.600">
                            Complete the Dates section first to enable calculation.
                          </Text>
                        )}
                      </VStack>
                    );
                  })()}
                </Tabs.Content>
              </Box>
            </Tabs.Root>
          </Box>
        </Box>
      ) : (
        // Small/Normal mode: Icon buttons
        <>
          {/* Toggle Button - Small, only toggles mini/normal */}
          <Box
            pt={3}
            px={sidebarMode === 'small' ? 2 : 4}
            pb={4}
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            <HStack justify="flex-start" gap={2}>
              <IconButton
                aria-label={sidebarMode === 'small' ? 'Expand sidebar' : 'Collapse sidebar'}
                size="sm"
                variant="ghost"
                onClick={toggleSidebarMode}
                _hover={{ bg: 'gray.100' }}
              >
                {sidebarMode === 'small' ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </IconButton>
            </HStack>
          </Box>

          {/* Content - Small/Normal mode: Icon buttons */}
          <VStack
            gap={LAYOUT_SPACING.ELEMENT_GAP}
            pt={4}
            px={sidebarMode === 'small' ? 2 : 4}
            pb={3}
            width="100%"
          >
            {iconButtons.map((button) => {
              // Show debt icon if on existing debt page OR if project has existing debt
              if (button.id === 'debt' && !hasExistingDebt && !isOnExistingDebtPage) {
                return null;
              }

              if (sidebarMode === 'small') {
                // Small mode: Icon only
                return (
                  <IconButton
                    key={button.id}
                    aria-label={`${button.label} section - ${button.status}`}
                    size="sm"
                    bg={getBgForStatus(button.status)}
                    color={getTextColorForStatus(button.status)}
                    _hover={{
                      bg: getHoverBgForStatus(button.status),
                    }}
                    variant="solid"
                    disabled={button.status === 'empty'}
                    onClick={() => handleIconClick(button.id)}
                    width="100%"
                  >
                    <button.icon size={18} />
                  </IconButton>
                );
              } else {
                // Normal mode: Icon + Label
                return (
                  <HStack
                    key={button.id}
                    as="button"
                    onClick={() => handleIconClick(button.id)}
                    width="100%"
                    bg={getBgForStatus(button.status)}
                    color={getTextColorForStatus(button.status)}
                    _hover={{
                      bg: getHoverBgForStatus(button.status),
                    }}
                    p={2}
                    borderRadius="md"
                    gap={2}
                    cursor={button.status === 'empty' ? 'not-allowed' : 'pointer'}
                    opacity={button.status === 'empty' ? 0.5 : 1}
                    transition={`all ${LAYOUT_SPACING.TRANSITION_FAST}`}
                  >
                    <button.icon size={18} />
                    <Text fontSize="sm" fontWeight="medium" flex={1} textAlign="left">
                      {button.label}
                    </Text>
                  </HStack>
                );
              }
            })}
          </VStack>
        </>
      )}
    </Box>
  );
};
